import * as React from "react";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { firebaseAuth } from "../../../utils/firestore";
import {
    browserLocalPersistence,
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    signInWithPopup,
} from "firebase/auth";
import styles from "./LoginPage.module.scss";
import { Button } from "../bits/Button/Button";
import { Input } from "../bits/Input/Input";
import GoogleIcon from "../icons/Google";
import EyeOffIcon from "../icons/EyeOff";
import EyeIcon from "../icons/Eye";
import Loader from "../bits/Loader/Loader";

export default function LoginPage() {
    const history = useHistory();

    const { addToast } = useToasts();
    const [signInInfo, setSignInInfo] = React.useState({
        email: "",
        password: "",
    });
    const [passwordVisible, setPasswordVisible] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleEmailChange = (e: React.FormEvent<HTMLInputElement>) => {
        setSignInInfo({ ...signInInfo, email: e.currentTarget.value });
    };

    const handlePasswordChange = (e: React.FormEvent<HTMLInputElement>) => {
        setSignInInfo({ ...signInInfo, password: e.currentTarget.value });
    };

    const loginWithPopup = async () => {
        try {
            setIsLoading(true);
            await firebaseAuth.setPersistence(browserLocalPersistence);
            const googleAuthProvider = new GoogleAuthProvider();
            googleAuthProvider.addScope("profile");
            googleAuthProvider.addScope("email");
            const authResult = await signInWithPopup(
                firebaseAuth,
                googleAuthProvider
            );
            if (authResult.user?.email) {
                await authResult.user.reload();
                const uiVersion = localStorage.getItem("ui_version");
                history.push(uiVersion === "v2" ? "/v2/main" : "/main");
            }
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false);
        }
    };

    const loginWithEmail = async () => {
        try {
            setIsLoading(true);
            const authResult = await signInWithEmailAndPassword(
                firebaseAuth,
                signInInfo.email,
                signInInfo.password
            );
            if (authResult.user?.email) {
                const uiVersion = localStorage.getItem("ui_version");
                history.push(uiVersion === "v2" ? "/v2/main" : "/main");
            }
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            addToast(
                `${
                    err.message ===
                        "Firebase: Error (auth/network-request-failed)." ||
                    "Firebase: Error (auth/user-not-found)."
                        ? "An email with this password does not exist"
                        : err.message
                }
            `,
                { appearance: "error" }
            );
        }
    };

    if (isLoading) {
        return <Loader show type="page" />;
    }

    return (
        <div className={styles.loginPage}>
            <article className={styles.loginPageCard}>
                <div className={styles.systemLabel}>
                    <span className={styles.systemLabelText}>IS</span>
                    INTAKE SYSTEM
                </div>
                <div className={styles.formContainer}>
                    <Button
                        onClick={() => {
                            loginWithPopup();
                        }}
                        variant="secondary"
                        iconLeft={<GoogleIcon />}
                    >
                        Sign-in with Google
                    </Button>
                    <div className={styles.divider}>OR</div>
                    <div className={styles.loginForm}>
                        <Input
                            label="Email Address"
                            placeholder="Email Address"
                            aria-label={"Email"}
                            id={"email"}
                            name={"email"}
                            onChange={handleEmailChange}
                        />

                        <Input
                            label="Password"
                            placeholder="Password"
                            aria-label={"Password"}
                            name={"password"}
                            id={"password"}
                            onChange={handlePasswordChange}
                            type={passwordVisible ? "text" : "password"}
                            endIcon={
                                passwordVisible ? (
                                    <Button
                                        variant="normal"
                                        className={styles.eyeButton}
                                        aria-label="Hide password"
                                        onClick={() => setPasswordVisible(false)}
                                    >
                                        <EyeOffIcon />
                                    </Button>
                                ) : (
                                    <Button
                                        variant="normal"
                                        className={styles.eyeButton}
                                        aria-label="Show password"
                                        onClick={() => setPasswordVisible(true)}
                                    >
                                        <EyeIcon />
                                    </Button>
                                )
                            }
                        />

                        <Button onClick={loginWithEmail}>Sign-in</Button>
                    </div>
                </div>
            </article>
        </div>
    );
}
